import { createContext, useContext, useState, useEffect } from 'react'
import { authApi, AuthError, AUTH_ERROR_CODES, getAuthErrorMessage } from '../api/auth'
import { STORAGE_KEYS } from '../utils/constants'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errorCode, setErrorCode] = useState(null)

  useEffect(() => {
    // Check for stored user data
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER)
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser))
        // Verify token is still valid
        authApi.getCurrentUser()
          .then((response) => {
            if (response.success && response.data) {
              setUser(response.data)
            } else {
              // Token invalid, clear storage
              localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
              localStorage.removeItem(STORAGE_KEYS.USER)
              setUser(null)
            }
          })
          .catch((err) => {
            // Check for session-related errors
            if (err.response?.status === 403 && err.response?.data?.error_code) {
              const { error_code, message } = err.response.data
              setErrorCode(error_code)
              setError(message || getAuthErrorMessage(error_code))
              // Clear local storage for blocked/locked users
              localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
              localStorage.removeItem(STORAGE_KEYS.USER)
            }
            setUser(null)
          })
          .finally(() => {
            setLoading(false)
          })
      } catch (error) {
        setUser(null)
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }, [])

  const clearError = () => {
    setError(null)
    setErrorCode(null)
  }

  const login = async (email, password) => {
    clearError()
    try {
      const response = await authApi.login(email, password)
      if (response.success && response.data) {
        setUser(response.data.user)
        return { success: true }
      }
      return { success: false, message: response.message || 'Login failed' }
    } catch (err) {
      // Handle AuthError with error code
      if (err instanceof AuthError) {
        setErrorCode(err.errorCode)
        setError(err.message)
        return {
          success: false,
          message: err.message,
          errorCode: err.errorCode,
          isSessionError: true,
        }
      }
      
      // Handle other errors (including 422 validation errors with field details)
      const message = err.response?.data?.message || 'Login failed'
      const errors = err.response?.data?.errors || null
      setError(message)
      return {
        success: false,
        message,
        errors,
      }
    }
  }

  const register = async (data) => {
    clearError()
    try {
      const response = await authApi.register(data)
      if (response.success && response.data) {
        setUser(response.data.user)
        return { success: true }
      }
      return { success: false, message: response.message || 'Registration failed' }
    } catch (err) {
      // Handle AuthError with error code
      if (err instanceof AuthError) {
        setErrorCode(err.errorCode)
        setError(err.message)
        return {
          success: false,
          message: err.message,
          errorCode: err.errorCode,
          isSessionError: true,
        }
      }
      
      // Handle other errors (including 422 validation errors with field details)
      const message = err.response?.data?.message || 'Registration failed'
      const errors = err.response?.data?.errors || null
      setError(message)
      return {
        success: false,
        message,
        errors,
      }
    }
  }

  const logout = async () => {
    await authApi.logout()
    setUser(null)
    clearError()
  }

  // Force logout when session is invalidated
  const forceLogout = (message = null) => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.USER)
    setUser(null)
    setErrorCode(AUTH_ERROR_CODES.SESSION_INVALID)
    setError(message || 'Your session has been terminated')
  }

  const value = {
    user,
    loading,
    error,
    errorCode,
    login,
    register,
    logout,
    forceLogout,
    clearError,
    isAuthenticated: !!user,
    // Session error checks
    isUserBlocked: errorCode === AUTH_ERROR_CODES.USER_BLOCKED,
    isSessionLocked: errorCode === AUTH_ERROR_CODES.SESSION_LOCKED,
    isAlreadyLoggedIn: errorCode === AUTH_ERROR_CODES.ALREADY_LOGGED_IN,
    isSessionInvalid: errorCode === AUTH_ERROR_CODES.SESSION_INVALID,
    isSessionError: [
      AUTH_ERROR_CODES.USER_BLOCKED,
      AUTH_ERROR_CODES.SESSION_LOCKED,
      AUTH_ERROR_CODES.ALREADY_LOGGED_IN,
      AUTH_ERROR_CODES.SESSION_INVALID,
    ].includes(errorCode),
    // Utility function
    getErrorMessage: () => errorCode ? getAuthErrorMessage(errorCode) : error,
  }

  // Always provide a value, even during initialization
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Re-export for convenience
export { AUTH_ERROR_CODES, getAuthErrorMessage }
